import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, computed} from "mobx";
import {ModalType, ShareType, SocialNetwork} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IModalMessage, TModalArguments} from "data/types/modals";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import {IUser} from "data/types/user";
import {ShareUtility} from "data/utils/social_share/social_share.utility";
import {find} from "lodash";
import type {IGameplayStore} from "data/stores/gameplay/gameplay.store";

export interface IModalsController extends ViewController {
	show: (...args: TModalArguments) => void;
	close: () => void;
	get user(): IUser | undefined;
	get visibleModal(): ModalType | null;
	get modalContent(): IModalMessage | null;
	get i18n(): ILocalizationStore;

	shareTw: (contestId: number | undefined) => void;
	shareFB: (contestId: number | undefined) => void;
}

@injectable()
export class ModalsController implements IModalsController {
	constructor(
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) public i18n: ILocalizationStore,
		@inject(Bindings.GameplayStore) public _gameplayStore: IGameplayStore
	) {
		makeAutoObservable(this);
	}

	get user(): IUser | undefined {
		return this._userStore.user;
	}

	get visibleModal() {
		return this._modalsStore.modal;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	show = (...args: TModalArguments) => {
		this._modalsStore.showModal(...args);
	};

	close = () => this._modalsStore.hideModal();

	@computed currentContest(contestId: number) {
		return find(this._gameplayStore.allContests, {id: contestId}) || null;
	}

	_shareMessage(contestId: number) {
		const contest = this.currentContest(contestId);
		return this.i18n.t("share.twitter.contest_text", {
			contest_name: this.i18n.findValueByLocaleObject(contest?.name ?? {}),
			X: this.i18n.findValueByLocaleObject(contest?.name ?? {}),
		});
	}

	shareTw = (contestId: number | undefined) => {
		if (contestId) {
			ShareUtility.share({
				message: this._shareMessage(contestId),
				type: ShareType.CONTEST,
				contestId: contestId,
				socialNetwork: SocialNetwork.TWITTER,
				locale: this.i18n.locale,
			});
		}
	};

	shareFB = (contestId: number | undefined) => {
		if (contestId) {
			ShareUtility.share({
				message: this._shareMessage(contestId),
				type: ShareType.CONTEST,
				contestId: contestId,
				socialNetwork: SocialNetwork.FACEBOOK,
				locale: this.i18n.locale,
			});
		}
	};

	dispose(): void {
		return;
	}

	init(): void {
		return;
	}
}
